import React from 'react';
import { createPortal } from 'react-dom';

import { MaskIcon } from '../MaskIcon';
import { ANIM } from '../../../../theme/tokens';
import { Surface, Scroll, Items, Fade, Row, RowIcon, RowLabel } from './MoreMenu.styles';

/**
 * The side-menu "More" overflow flyout — the popup that holds the nav items the
 * rail can't fit (Figma node 8927:821). Rendered in a portal to <body> and
 * positioned `fixed` from the trigger's viewport rect, so it floats above the
 * content card (it would otherwise sit under the page's transition/stacking
 * context) and clamps to the viewport — when the list is taller than the space
 * above the trigger, the inner scroller takes over, with top/bottom fade masks
 * so it never looks hard-cut.
 *
 * Purely presentational otherwise: the rail (`SideMenu`) owns the open state,
 * decides which items overflow, and handles outside-click / Escape.
 */
export type MoreMenuItem = {
  key: string;
  href: string;
  label: string;
  /** Remote SVG, rendered via MaskIcon (CSS mask) — the rail's own icon. */
  icon?: string;
  /** Local inline glyph — takes precedence over `icon` (wallet / casino / players). */
  glyph?: React.ReactElement;
};

export type MoreMenuProps = {
  open: boolean;
  items: MoreMenuItem[];
  /** The trigger (or its wrapper) — the flyout is positioned from its rect. */
  anchorRef: React.RefObject<HTMLElement>;
  /** Attached to the surface so the rail's outside-click test can exclude it. */
  surfaceRef?: React.RefObject<HTMLDivElement>;
  /** Called when a row is activated — the rail closes the flyout. */
  onNavigate: () => void;
  /** id of the trigger button, for `aria-labelledby`. */
  labelledBy?: string;
  id?: string;
};

type Pos = { left: number; bottom: number; maxHeight: number };

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 10;

export const MoreMenu = ({
  open,
  items,
  anchorRef,
  surfaceRef,
  onNavigate,
  labelledBy,
  id,
}: MoreMenuProps): React.ReactElement | null => {
  // `mounted` keeps the node in the tree through the exit animation; `shown`
  // drives the open/closed CSS state (flipped a frame after mount so the enter
  // transition runs from the closed state).
  const [mounted, setMounted] = React.useState(open);
  const [shown, setShown] = React.useState(false);
  const [pos, setPos] = React.useState<Pos | null>(null);
  const [fades, setFades] = React.useState({ top: false, bottom: false });

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Anchor the flyout to the trigger: just to its right, bottom-aligned so it
  // opens upward, with a height capped to the space above (then it scrolls).
  const place = React.useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    setPos({
      left: r.right + ANCHOR_GAP,
      bottom: Math.max(VIEWPORT_MARGIN, window.innerHeight - r.bottom),
      maxHeight: Math.max(120, Math.min(r.bottom - VIEWPORT_MARGIN, window.innerHeight - 2 * VIEWPORT_MARGIN)),
    });
  }, [anchorRef]);

  const updateFades = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setFades({
      top: el.scrollTop > 1,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 1,
    });
  }, []);

  React.useEffect(() => {
    if (open) {
      place();
      setMounted(true);
      const raf = requestAnimationFrame(() => {
        setShown(true);
        place();
        updateFades();
      });
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), ANIM.MENU_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open, place, updateFades]);

  // Keep the flyout pinned to its (fixed) trigger across viewport changes.
  React.useEffect(() => {
    if (!mounted) return undefined;
    const onChange = (): void => {
      place();
      updateFades();
    };
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [mounted, place, updateFades]);

  if (!mounted) return null;

  return createPortal(
    <Surface
      id={id}
      ref={surfaceRef}
      role="menu"
      aria-labelledby={labelledBy}
      $open={shown}
      style={pos ? { left: pos.left, bottom: pos.bottom, maxHeight: pos.maxHeight } : { visibility: 'hidden' }}
    >
      <Fade $edge="top" $visible={fades.top} aria-hidden />
      <Scroll ref={scrollRef} onScroll={updateFades}>
        <Items>
          {items.map((item, index) => (
            <Row key={item.key} to={item.href} role="menuitem" $index={index} $open={shown} onClick={onNavigate}>
              <RowIcon className="icon">{item.glyph ?? <MaskIcon className="icon-mask" src={item.icon ?? ''} />}</RowIcon>
              <RowLabel className="txt" text={item.label} />
            </Row>
          ))}
        </Items>
      </Scroll>
      <Fade $edge="bottom" $visible={fades.bottom} aria-hidden />
    </Surface>,
    document.body,
  );
};

export default MoreMenu;
